import React from 'react'
import styled from 'styled-components'
import { LegacyQuizScreen, QuizVersion } from '../../config/types'
import { ProgressBar } from './ProgressBar'
import { ContentContainer } from '../core/styled/ContentContainer'
import { DSButton, DSIcon } from '@zoe/ds-web'
import { HEADER_BAR_HEIGHT } from '../core/constants'

interface HeaderBarProps {
  currentPageIndex: number
  hideProgressBar: boolean
  screens: LegacyQuizScreen[]
  quizVersion: QuizVersion
  overlayHeader?: boolean
  onBackClick?: () => void
}

const HeaderWrapper = styled.div<{ $menoscaleResults: boolean; $overlay?: boolean }>`
  height: ${HEADER_BAR_HEIGHT};
  display: flex;
  align-items: center;
  padding: 0 var(--grid-16);
  /* Sit above a full-bleed topImage that is pulled up behind the header, so the
     logo stays visible over the photo rather than being painted over by it. */
  ${({ $overlay }) =>
    $overlay &&
    `
    position: relative;
    z-index: 2;
    background-color: transparent;
  `}
  ${({ $menoscaleResults }) =>
    $menoscaleResults &&
    `
    background-color: #532d3c;
    @media (min-width: 768px) {
      background-color: transparent;
    }
  `}
`

const HeaderContent = styled(ContentContainer)`
  display: flex;
  align-items: center;
  margin: 0 auto;
  position: relative;
`

const BackButtonWrapper = styled.div`
  position: absolute;
  left: 0;
`

const LogoCentered = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`

// TODO: the back chevron gets no matching light treatment, so overlayHeader is only
// safe on screens without a back button.
const LogoImg = styled.img<{ $light?: boolean }>`
  height: 24px;
  /* The logo is a single-colour SVG loaded via <img>, so it can't be recoloured
     by fill. Invert it to white for legibility over a dark full-bleed photo. */
  ${({ $light }) => $light && `filter: brightness(0) invert(1);`}
`

const ProgressBarContainer = styled(ContentContainer)`
  display: flex;
  align-content: center;
  margin: 0 auto;
  /* ContentContainer's 24px vertical padding pushed the bar too far from the
     header. Double-up the selector specificity (&&) so we win the cascade
     against the base shorthand at every viewport width. */
  && {
    padding-top: var(--grid-8);
    padding-bottom: var(--grid-8);
  }
`

const ProgressBarInner = styled.div`
  width: 100%;
`

export const HeaderBar: React.FC<HeaderBarProps> = ({
  screens,
  currentPageIndex,
  hideProgressBar,
  quizVersion,
  overlayHeader,
  onBackClick,
}) => {
  const remainingScreens = screens.slice(currentPageIndex + 1).filter((screen) => screen.isIncludedInProgress).length
  const currentCompletion = currentPageIndex / Math.max(currentPageIndex + remainingScreens, 1)
  const scaling = 'linear' as const

  const onMenoscaleResultsScreen = quizVersion === 'menoscale' && currentPageIndex === screens.length - 1

  return (
    <>
      <HeaderWrapper $menoscaleResults={onMenoscaleResultsScreen} $overlay={overlayHeader}>
        <HeaderContent>
          {onBackClick && (
            <BackButtonWrapper data-testid="back-icon-button">
              <DSButton variant="tertiary" colorScheme="neutral" size="small" width="auto" onPress={onBackClick}>
                <DSIcon name="chevron-left" size={24} />
              </DSButton>
            </BackButtonWrapper>
          )}
          <LogoCentered>
            <LogoImg src="/img/shared/zoe-logo.svg" alt="ZOE logo" $light={overlayHeader} />
          </LogoCentered>
        </HeaderContent>
      </HeaderWrapper>
      {hideProgressBar || (
        <ProgressBarContainer>
          <ProgressBarInner>
            <ProgressBar completionPercentage={currentCompletion} scaling={scaling} color="#FFD100" />
          </ProgressBarInner>
        </ProgressBarContainer>
      )}
    </>
  )
}
